import { InMemoryCache, Reference, FieldMergeFunction } from '@apollo/client';
import type { KeyFieldsFunction } from '@apollo/client/cache/inmemory/policies';
import MetricTracking from 'lib/MetricTracking';
import _ from 'lodash';
import hqConvoTypePolicies from 'conversations/gql/cache';
import {
  hqChatMessageTemplatePolicies,
  hqOrganizationRepresentativesPolicies,
} from 'messageTemplates/gql/cache';

/**
 * Important docs
 *
 * Default cache ids https://www.apollographql.com/docs/react/caching/overview/#2-generate-cache-ids
 *
 */

// we need to tell apollo how to handle the merge of this array
// in particular, sequenceItems depend on their `seq` fields,
// this logic is to sort those arrays according to seq
export const mergeSequenceItems: FieldMergeFunction<Array<Reference>, Array<Reference>> = (
  existing = [],
  incoming = [],
  options
) => {
  // There are certain conditions where we fetch a list of messages (seqItems)
  // and there's like a race condition between this merge function and apollo's cache
  // and so most of those messages will return `seq` as undefined making our algorithm
  // to sort break everything. We tried other methods but this seems to hit the right
  // heuristic spot where it mostly works.
  //
  // existing is an empty array typically when we are first fetching a page of sequenceItems
  // so we shouldn't need to sort and all those things anyways.
  if (!existing.length) {
    return incoming;
  }

  const items = [...incoming, ...existing].sort((a, b) => {
    // new items come with seq undefined because they aren't in cache yet
    const seqA = Number(options.readField('seq', a) ?? Infinity);
    const seqB = Number(options.readField('seq', b) ?? Infinity);

    if (seqA > seqB) {
      return -1;
    }

    if (seqA < seqB) {
      return 1;
    }

    return 0;
  });

  return _.uniqWith(items, _.isEqual);
};

/**
 * Simple merge function to merge arrays
 */
export const mergeArray: FieldMergeFunction<Array<Reference>, Array<Reference>> = (
  existing = [],
  incoming = []
) => {
  return [...existing, ...incoming];
};

/**
 * Simple merge function to merge arrays but removing duplicated,
 * useful for some paginated queries that might return duplicated items (the last and the first)
 */
export const mergeArrayUniq: FieldMergeFunction<any, any> = (existing = [], incoming = []) => {
  return _.uniqWith([...existing, ...incoming], _.isEqual);
};

/**
 * This is a @deprecated api, DO NOT add stuff to this, instead use the `typePolicies` specified below.
 */
const dataIdFromObject: KeyFieldsFunction = (object) => {
  // by default do not normalize
  return false;
};

export function newInMemoryCache() {
  // https://www.apollographql.com/docs/react/caching/cache-configuration
  return new InMemoryCache({
    dataIdFromObject,
    // https://www.apollographql.com/docs/react/data/fragments/#defining-possibletypes-manually
    possibleTypes: {
      NavigationSectionItem: ['Class', 'Organization'],
    },
    typePolicies: {
      // This is just a namespace for auto message related queries
      // namespace, most of the time, should be non normalized and merged
      AutoMessaging: {
        merge: true,
      },

      AutoMessage: {
        keyFields: ['uuid'],
        fields: {
          previousSends: {
            keyArgs: [],
          },
        },
      },

      AutoMessageSend: {
        keyFields: ['uuid'],
        merge: true,
      },

      AutoMessageSendConnection: {
        fields: {
          edges: {
            merge: mergeArrayUniq,
          },
        },
      },

      SingleDayAvailabilityOverride: {
        keyFields: ['uuid'],
      },

      NavigationListEntityItem: {
        keyFields: ['uuid'],
      },

      MessageItem: {
        keyFields: ['uuid'],
      },
      SftpFeatureCredentials: {
        keyFields: ['credentials', ['username']],
      },
      LearningToolsInteroperabilityIntegration: {
        keyFields: ['serviceKey'],
      },
      AdminIntegration: {
        keyFields: ['providerKey'],
      },
      PartnerIntegration: {
        keyFields: ['providerKey'],
      },
      ContentSource: {
        keyFields: ['providerKey'],
      },
      // SequenceItem's key is the item uuid + sequence id.
      // So it's should be unique and not collide with message uuids.
      SequenceItem: {
        // IMPORTANT if you change this then multiple other places in the app might break.
        // When a user writes a message we are doing fancy and hacky optimistic updates on the cache
        // that rely on this key. One thing we tried to make the `mergeSequenceItems` more robust
        // was to introduce the `seq` into the cache, but since we are calculatiing the seq optimistcally
        // then sometimes you get duplicated messages (key,localSeq), (key,serverSeq).
        keyFields: ['key'],
      },
      OrganizationMember: {
        keyFields: ['user', ['uuid']],
      },
      ContentSourceProvider: {
        keyFields: ['providerKey'],
      },
      LinkPreview: {
        keyFields: ['url'],
      },
      LinkedAccount: {
        keyFields: ['provider'],
      },
      ReactionSummary: {
        keyFields: ['messageUuid'],
        fields: {
          reactions: { merge: false },
        },
      },

      ProductLimits: {
        merge: true,
      },

      LanguageSet: {
        keyFields: ['targetUuid'],
      },

      // sequenceItems is a list that needs to be ordered
      // by its element.seq attribute, this tells apollo to do that
      Stream: {
        keyFields: ['uuid'],
        fields: {
          // this is important for pagination
          sequenceItems: {
            keyArgs: [],
            merge: mergeSequenceItems,
          },
        },
      },
      ChatStream: {
        keyFields: ['uuid'],
        fields: {
          permissions: {
            merge: true,
          },
          // this is important for pagination
          sequenceItems: {
            keyArgs: [],
            merge: mergeSequenceItems,
          },
        },
      },
      GroupThreadStream: {
        keyFields: ['uuid'],
        fields: {
          // this is important for pagination
          sequenceItems: {
            keyArgs: [],
            merge: mergeSequenceItems,
          },
        },
      },
      EntityStream: {
        keyFields: ['uuid'],
        fields: {
          // this is important for pagination
          sequenceItems: {
            keyArgs: [],
            merge: mergeSequenceItems,
          },
        },
      },

      User: {
        keyFields: ['uuid'],
        fields: {
          profile_picture: {
            merge: true,
          },
          preferences: {
            merge: true,
          },
          organizations: {
            keyArgs: [
              'type',
              'affiliation_status',
              'include_transitive_organizations',
              'onlyAdminned',
              'onlyPaid',
            ],
          },
        },
      },

      /**
       * You are going to see a bunch of types the same as this one, so the same
       * explanation will apply from them.
       *
       * Some of this nested types do not have a natural id but they rely on
       * the id of the parent object, such as `UserPermissions`, so we mark them
       * as "do not normalize" and we also tell apollo to merge any new changes into the existing
       * values as opposed of replacing.
       *
       * Some others such as `Settings` are plain old singletons which means that they are
       * contextual to the user session.
       */
      Settings: {
        keyFields: [],
      },

      // Special stuff that needs auto merging becuase we fiddle with rest api and apollo cache
      GroupAvatar: {
        merge: true,
      },

      UserPermissions: {
        merge: true,
      },

      GroupPermissions: {
        merge: true,
      },

      Organization: {
        keyFields: ['uuid'],
        fields: {
          permissions: {
            merge: true,
          },

          childOrganizations: {
            merge: mergeArrayUniq,
          },

          classes: {
            keyArgs: ['sortType', 'sortDirection', 'query'],
          },

          members: {
            keyArgs: [
              'query',
              'roles',
              'userIds',
              'grade',
              'rostered',
              'ownersOnly',
              'includeUserStats',
              'sortType',
              'sortDirection',
              'deviceType',
            ],
          },

          unclaimedGroups: {
            keyArgs: ['query', 'sortType', 'sortDirection'],
          },

          roleRequests: {
            keyArgs: ['role'],
          },
        },
      },

      OrgAdminDescription: {
        keyFields: ['uuid'],
      },

      ScheduledMessage: {
        keyFields: ['uuid'],
      },

      // needed by Organization.fields.classes
      OrganizationGroupsListConnection: {
        fields: {
          classes: {
            merge: mergeArrayUniq,
          },
        },
      },

      // needed by Organization.fields.members
      OrganizationMembersListConnection: {
        fields: {
          members: {
            merge: mergeArrayUniq,
          },
        },
      },

      // needed by Organization.fields.unclaimedGroups
      OrganizationUnclaimedGroupsListConnection: {
        fields: {
          unclaimedGroups: {
            merge: mergeArrayUniq,
          },
        },
      },

      // needed by Organization.fields.roleRequests
      RoleRequestsListConnection: {
        fields: {
          roleRequests: {
            merge: mergeArrayUniq,
          },
        },
      },

      Class: {
        keyFields: ['uuid'],
        fields: {
          // Good example of how to handle paginated queries in our system, read more here
          // https://www.apollographql.com/docs/react/pagination/core-api/
          membersPaginated: {
            keyArgs: ['q', 'sortType', 'sortDirection', 'orgRoles', 'ownersOnly', 'userIds'],
          },
        },
      },

      // needed by Query.fields.groupMembershipRequests
      GroupMembershipRequestsListConnection: {
        fields: {
          groupMembershipRequests: {
            merge: mergeArrayUniq,
          },
        },
      },

      // needed by Class.fields.membersPaginated
      GroupMembersListConnection: {
        fields: {
          members: {
            merge: mergeArrayUniq,
          },
        },
      },

      ClassMembership: {
        keyFields: ['user', ['uuid']],
      },

      // needed by Query.fields.api_pendingRoleRequests
      PendingRoleRequestsOutput: {
        fields: {
          roleRequests: {
            merge: mergeArrayUniq,
          },
        },
      },

      ClassMembershipPermissions: {
        merge: true,
      },

      OrgUserAffiliation: {
        merge: true,
      },

      RelatedUser: {
        keyFields: ['uuid'],
        fields: {
          reachabilityInfo: { merge: true },
        },
      },

      EntitySender: {
        keyFields: ['uuid'],
      },

      UserSender: {
        keyFields: ['uuid'],
      },

      BotSender: {
        keyFields: ['uuid'],
      },

      Translations: {
        // This thing has a weird shape
        merge: true,
      },

      StudentIdList: {
        keyFields: ['uuid'],
        fields: {
          items: {
            keyArgs: ['identified'],
          },
        },
      },

      // needed by StudentIdList.items
      StudentIdListItemsConnection: {
        fields: {
          edges: {
            merge: mergeArrayUniq,
          },
        },
      },

      /**
       *  THIS BELONGS TO HQ SCHEMA
       */
      ChatMessageTemplate: hqChatMessageTemplatePolicies.ChatMessageTemplates,

      /**
       *  THIS BELONGS TO HQ SCHEMA
       */
      ChatMessageTemplateRepresentedOrganizationsPayload:
        hqOrganizationRepresentativesPolicies.ChatMessageTemplateRepresentedOrganizationsHQ,
      /**
       *  THIS BELONGS TO HQ SCHEMA
       */
      ConversationMessagesView: hqConvoTypePolicies.ConversationMessagesView,
      /**
       *  THIS BELONGS TO HQ SCHEMA
       */
      ConversationsListView: hqConvoTypePolicies.ConversationsListView,

      Query: {
        fields: {
          /**
           *  THIS BELONGS TO HQ SCHEMA
           */
          chatMessageTemplateRepresentedOrganizations:
            hqOrganizationRepresentativesPolicies.Query.fields!
              .chatMessageTemplateRepresentedOrganizations,
          /**
           *  THIS BELONGS TO HQ SCHEMA
           */
          conversationMessages: hqConvoTypePolicies.Query.fields!.conversationMessages,
          /**
           *  THIS BELONGS TO HQ SCHEMA
           */
          conversationsList: hqConvoTypePolicies.Query.fields!.conversationsList,

          groupMembershipRequests: {
            keyArgs: ['nameQuery'],
          },

          api_pendingRoleRequests: {
            keyArgs: ['organizationUuid', 'role'],
          },

          // see docs and tests in src/lib/apollo/cache/resolver/__tests__/cachedChatStreams-test.ts
          cachedChatStreams: {
            // there's no better way than dumping the whole cache
            // https://github.com/apollographql/apollo-client/issues/4724
            read: (_, options) => {
              const rawCache = options.cache.extract();
              const cachedChatStreams = Object.keys(rawCache)
                .filter((key) => key.startsWith('ChatStream'))
                .map((key) => rawCache[key]);
              return cachedChatStreams
                .filter(Boolean)
                .map((stream) => options.toReference(stream!));
            },
          },

          // see src/lib/apollo/cache/resolver/__tests__/chatStreams-test.ts
          chatStreams: {
            keyArgs: ['groupId', 'chatQuery', 'chatUuids'],
            // chatStreams is an array of ChatStream, we need to tell apollo how to handle
            // the merge of this array
            // order is not important
            merge: mergeArrayUniq,
          },

          // see src/lib/apollo/cache/resolver/__tests__/organizations-test.ts
          organizations: {
            // see https://www.apollographql.com/docs/react/caching/cache-field-behavior#the-read-function
            read: (_, { args, cache, toReference }) => {
              const uuids: Array<string> = args?.uuids;
              if (!uuids || !Array.isArray(uuids)) {
                return;
              }
              MetricTracking.increment('apollo_cache.organizations.read');

              const ids: Array<string | undefined> = uuids.map((uuid) =>
                cache.identify({ __typename: 'Organization', uuid })
              );

              // The toReference helper function always returns a reference object
              //   regardless of whether a value for that reference can be found in the cache
              // This code filters out the reference objects that do not resolve to any values in the cache
              const orgsInCache = Object.keys(cache.extract()).filter((key) =>
                key.startsWith('Organization:')
              );
              const references = ids
                .map((id) => toReference(id!, false))
                .filter((ref) => ref && orgsInCache.includes(ref.__ref));

              // The apollo magic only does a network query if a null comes back from this function
              // When we do an organizations query with a list of uuids as an argument, we expect to get back an value for every uuid
              //    so we have to return null if *any* of our organizations was not found in the cache or the list will be returned
              //    with that value missing
              if (references.length < uuids.length) {
                if (references.length === 0) {
                  MetricTracking.increment('apollo_cache.organizations.read.cached.none');
                } else {
                  MetricTracking.increment('apollo_cache.organizations.read.cached.some');
                }
                return;
              }
              MetricTracking.increment('apollo_cache.organizations.read.cached.all');
              return references;
            },
          },
        },
      },
    },
  });
}
